import React, { useEffect, useRef } from 'react';
import mermaid from 'mermaid';

interface MermaidDiagramProps {
  chart: string;
  className?: string;
}

export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ chart, className }) => {
  const mermaidRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chart || !mermaidRef.current) return;

    // Check if dark mode is enabled
    const isDarkMode = document.documentElement.classList.contains('dark');

    mermaid.initialize({
      startOnLoad: true,
      theme: isDarkMode ? 'dark' : 'default',
      securityLevel: 'loose',
      themeVariables: isDarkMode
        ? {
            fontFamily: 'inherit',
            fontSize: '16px',
            primaryColor: '#3b82f6',
            primaryTextColor: '#ffffff',
            primaryBorderColor: '#60a5fa',
            lineColor: '#94a3b8',
            secondaryColor: '#1e293b',
            tertiaryColor: '#334155',
            mainBkg: '#1e293b',
            errorBkgColor: '#991b1b',
            errorTextColor: '#fecaca',
            nodeTextColor: '#f8fafc',
            nodeBkg: '#334155',
            clusterBkg: '#1e293b',
            clusterBorder: '#475569',
            labelBoxBkgColor: '#334155',
            labelBoxBorderColor: '#475569',
            labelTextColor: '#f8fafc',
            titleColor: '#f8fafc',
            edgeLabelBackgroundColor: '#334155',
            edgeLabelColor: '#f8fafc',
          }
        : undefined,
    });

    // Clear the container first
    mermaidRef.current.innerHTML = '';

    // Remove any Markdown code fence markers and 'mermaid' keyword
    const cleanChart = chart
      .replace(/^```(?:mermaid)?\s*\n?/m, '') // Remove opening fence
      .replace(/\n?```\s*$/m, '') // Remove closing fence
      .trim(); // Clean up any extra whitespace

    mermaid
      .render('mermaid-diagram', cleanChart)
      .then(({ svg }) => {
        if (mermaidRef.current) {
          mermaidRef.current.innerHTML = svg;

          // Add custom CSS for better edge visibility in dark mode
          if (isDarkMode) {
            const style = document.createElement('style');
            style.textContent = `
              .mermaid-diagram path {
                stroke: #94a3b8 !important;
                stroke-width: 2px !important;
              }
              .mermaid-diagram .edgeLabel {
                background-color: #1e293b !important;
                color: #f8fafc !important;
              }
              .mermaid-diagram .node rect {
                stroke: #60a5fa !important;
                stroke-width: 2px !important;
              }
              .mermaid-diagram .node text {
                fill: #f8fafc !important;
              }
            `;
            mermaidRef.current.appendChild(style);
          }
        }
      })
      .catch((error) => console.error('Mermaid rendering failed:', error));
  }, [chart]);

  return <div ref={mermaidRef} className={className} />;
};
